import { AbstractControl, FormArray, ValidationErrors } from '@angular/forms';

const MAX_NOT_ABOVE_MIN = 'maxNotAboveMin';
const PRICE_NOT_BELOW_BASE = 'priceNotBelowBase';
const PRICE_NOT_DESCENDING = 'priceNotDescending';

function isFilled(value: unknown): boolean {
    return value !== null && value !== undefined && value !== '';
}

/**
 * Cross-field wholesale errors live on the control that should turn red.
 * Parent validators call this while they run; emitEvent stays off so that
 * write does not start another validation pass.
 */
function setControlError(control: AbstractControl | null, key: string, active: boolean): void {
    if (!control) {
        return;
    }
    const current = control.errors;
    const next: ValidationErrors = { ...(current ?? {}) };
    if (active) {
        next[key] = true;
    } else {
        delete next[key];
    }
    const normalized = Object.keys(next).length > 0 ? next : null;
    if (JSON.stringify(current) === JSON.stringify(normalized)) {
        return;
    }
    control.setErrors(normalized, { emitEvent: false });
}

export function ValidateWholesalePriceLine(control: AbstractControl) {
    const value = control.getRawValue();
    const minFilled = isFilled(value.min);
    const maxFilled = isFilled(value.max);
    // Required covers an empty max. The message only applies once both quantities are filled.
    setControlError(
        control.get('max'),
        MAX_NOT_ABOVE_MIN,
        minFilled && maxFilled && Number(value.max) <= Number(value.min),
    );

    if (minFilled && Number(value.min) <= 0) {
        return { invalidMinPrice: true };
    }

    return null;
}

export function ValidateWholesale(control: AbstractControl) {
    const lines = control.get('wholesalePrices') as FormArray | null;
    const value = control.getRawValue();

    if (!lines || lines.length === 0) {
        return null;
    }

    let quantityOutOfOrder = false;

    for (let i = 0; i < lines.length; i++) {
        const line = lines.at(i);
        const priceControl = line.get('price');
        const wholesalePrice = value.wholesalePrices[i];
        const priceFilled = isFilled(wholesalePrice?.price);

        if (i === 0) {
            const sampleFilled = isFilled(value.samplePrice);
            setControlError(priceControl, PRICE_NOT_DESCENDING, false);
            setControlError(
                priceControl,
                PRICE_NOT_BELOW_BASE,
                priceFilled && sampleFilled && Number(wholesalePrice.price) >= Number(value.samplePrice),
            );
            continue;
        }

        const previous = value.wholesalePrices[i - 1];
        const previousPriceFilled = isFilled(previous?.price);
        // Later tiers are not compared to the base price.
        setControlError(priceControl, PRICE_NOT_BELOW_BASE, false);
        setControlError(
            priceControl,
            PRICE_NOT_DESCENDING,
            priceFilled && previousPriceFilled && Number(wholesalePrice.price) >= Number(previous.price),
        );

        if (wholesalePrice.min <= previous.min || wholesalePrice.max <= previous.max) {
            quantityOutOfOrder = true;
        }
    }

    return quantityOutOfOrder ? { invalidWholesalePrice: true } : null;
}
